import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { printHtmlSilently } from '../lib/silentPrint';
import { visibleMethods } from '../lib/closingMethods';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Printer, ClipboardList, Clock, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_LABELS = {
  dinheiro: 'Dinheiro', pix: 'PIX (conta da loja)', pix_maquininha: 'PIX Maquininha',
  credito: 'Cartão de Crédito', debito: 'Cartão de Débito', cartao: 'Cartão (legado)',
  voucher: 'Voucher', cheque: 'Cheque', credito_loja: 'Créd. Loja', a_prazo: 'Venda a Prazo'
};
const methodsToShow = (s) => visibleMethods(s.breakdown_expected, s.counted_breakdown).map((m) => m.key);

const ShiftSummaryPage = () => {
  return (
    <Layout>
      <ShiftSummaryContent />
    </Layout>
  );
};

export const ShiftSummaryContent = () => {
  const { user } = useAuth();
  const today = new Date().toISOString().substring(0, 10);
  const firstDay = today.substring(0, 7) + '-01';
  const [start, setStart] = useState(firstDay);
  const [end, setEnd] = useState(today);
  const [operatorId, setOperatorId] = useState('');
  const [operators, setOperators] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/users/operators`, { withCredentials: true })
      .then(r => setOperators(r.data || []))
      .catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams({ start, end });
      if (operatorId) params.append('operator_id', operatorId);
      const r = await axios.get(`${API}/cash-sessions/shift-summary?${params.toString()}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao carregar resumo');
    } finally { setLoading(false); }
  };

  const handlePrint = (s) => {
    const bdExpected = s.breakdown_expected || {};
    const bdCounted = s.counted_breakdown || {};
    const bdDiff = s.breakdown_difference || {};

    const rows = methodsToShow(s).map(m => {
      const exp = Number(bdExpected[m] || 0);
      const cnt = Number(bdCounted[m] || 0);
      const dif = Number(bdDiff[m] || (cnt - exp));
      return `<tr>
        <td>${METHOD_LABELS[m] || m}</td>
        <td class="right">${exp.toFixed(2)}</td>
        <td class="right">${cnt.toFixed(2)}</td>
        <td class="right ${dif === 0 ? '' : (dif > 0 ? 'pos' : 'neg')}">${dif >= 0 ? '+' : ''}${dif.toFixed(2)}</td>
      </tr>`;
    }).join('');

    const html = `
      <div class="center bold" style="font-size:13px">RESUMO DE TURNO</div>
      <div class="center small">${s.operator_name}</div>
      <hr/>
      <table><tbody>
        <tr><td>Abertura:</td><td class="right small">${s.opened_at ? new Date(s.opened_at).toLocaleString('pt-BR') : '-'}</td></tr>
        <tr><td>Fechamento:</td><td class="right small">${s.closed_at ? new Date(s.closed_at).toLocaleString('pt-BR') : '-'}</td></tr>
        <tr><td>Tempo:</td><td class="right small">${s.hours_worked}h</td></tr>
        <tr><td>Terminal:</td><td class="right small">${s.terminal || '-'}</td></tr>
      </tbody></table>
      <hr/>
      <table><tbody>
        <tr><td>Valor inicial:</td><td class="right">R$ ${Number(s.initial_amount).toFixed(2)}</td></tr>
        <tr><td>N. vendas:</td><td class="right">${s.sales_count}</td></tr>
        <tr><td>Total vendido:</td><td class="right bold">R$ ${Number(s.total_sales).toFixed(2)}</td></tr>
      </tbody></table>
      <hr/>
      <div class="bold">CONCILIACAO</div>
      <table><thead>
        <tr><th class="left small">Forma</th><th class="right small">Esp.</th><th class="right small">Cont.</th><th class="right small">Dif.</th></tr>
      </thead><tbody>${rows}</tbody></table>
      <hr/>
      <table><tbody>
        <tr><td class="bold">Dinheiro esperado:</td><td class="right bold">R$ ${Number(s.expected_amount).toFixed(2)}</td></tr>
        <tr><td class="bold">Dinheiro contado:</td><td class="right bold">R$ ${Number(s.counted_amount).toFixed(2)}</td></tr>
        <tr><td class="bold">Dif. so dinheiro:</td><td class="right bold">${Number(s.cash_difference ?? s.difference) >= 0 ? '+' : ''}R$ ${Number(s.cash_difference ?? s.difference).toFixed(2)}</td></tr>
        <tr><td class="bold">DIFERENCA TOTAL (todas as formas):</td><td class="right bold ${Math.abs(Number(s.difference)) < 0.005 ? '' : (s.difference > 0 ? 'pos' : 'neg')}">${s.difference >= 0 ? '+' : ''}R$ ${Number(s.difference).toFixed(2)}</td></tr>
      </tbody></table>
      <hr/>
      <div class="center small">Smart Flow</div>
    `;

    printHtmlSilently(`
      <html><head><title>Resumo Turno ${s.operator_name}</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        body { font-family: 'Courier New', monospace; font-size: 11px; padding: 8px; color: #000; margin: 0; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 2px 0; vertical-align: top; }
        .right { text-align: right; } .center { text-align: center; } .left { text-align: left; }
        .bold { font-weight: bold; } .small { font-size: 10px; }
        .pos { color: #047857; } .neg { color: #b91c1c; }
      </style>
      </head><body>${html}</body></html>
    `);
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-300 rounded-md p-6 text-center" data-testid="shift-no-access">
          <AlertCircle className="mx-auto text-red-600 mb-3" size={48} />
          <h2 className="text-xl font-bold text-red-800">Acesso restrito</h2>
          <p className="text-sm text-red-700 mt-2">Esta página é exclusiva para administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="shift-summary-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-indigo-600 flex items-center justify-center">
            <ClipboardList className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Resumo de Turnos</h1>
            <p className="text-sm text-slate-600">Conciliação financeira por operador e período (admin)</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-md border border-slate-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">De</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="shift-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Até</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="shift-end" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Operador</Label>
              <select value={operatorId} onChange={(e) => setOperatorId(e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="shift-operator">
                <option value="">Todos</option>
                {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <Button onClick={load} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 h-10" data-testid="shift-load">
              {loading ? 'Carregando...' : 'Buscar'}
            </Button>
          </div>
        </div>

        {/* Totais */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-md border border-slate-200 p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Turnos no período</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="totals-sessions">{data.totals.sessions}</p>
            </div>
            <div className="bg-white rounded-md border border-slate-200 p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Vendas (un.)</p>
              <p className="text-2xl font-bold text-slate-900">{data.totals.sales_count}</p>
            </div>
            <div className="bg-white rounded-md border border-emerald-300 p-4">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Total vendido</p>
              <p className="text-2xl font-bold text-emerald-700">R$ {Number(data.totals.sales_total).toFixed(2)}</p>
            </div>
            <div className={`bg-white rounded-md border p-4 ${Math.abs(data.totals.diff_total) < 0.01 ? 'border-slate-200' : data.totals.diff_total > 0 ? 'border-blue-300' : 'border-amber-300'}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Diferença total</p>
              <p className={`text-2xl font-bold ${Math.abs(data.totals.diff_total) < 0.01 ? 'text-slate-700' : data.totals.diff_total > 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                {data.totals.diff_total >= 0 ? '+' : ''}R$ {Number(data.totals.diff_total).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {err && <div className="bg-red-50 border border-red-300 text-red-700 p-3 rounded mb-4">{err}</div>}

        {/* Lista de turnos */}
        {data && data.sessions.length === 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded p-8 text-center text-slate-500" data-testid="shift-empty">
            Nenhum turno fechado nesse período.
          </div>
        )}

        <div className="space-y-3" data-testid="shift-list">
          {data && data.sessions.map((s) => {
            const diff = Number(s.difference || 0);
            const diffColor = Math.abs(diff) < 0.01 ? 'text-slate-700 bg-slate-100' : diff > 0 ? 'text-blue-700 bg-blue-100' : 'text-amber-800 bg-amber-100';
            return (
              <div key={s.id} className="bg-white rounded-md border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`shift-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">{s.operator_name}</h3>
                    <p className="text-xs text-slate-500">
                      <Clock size={11} className="inline mr-1" />
                      {s.opened_at && new Date(s.opened_at).toLocaleString('pt-BR')}
                      &nbsp;→&nbsp;{s.closed_at && new Date(s.closed_at).toLocaleString('pt-BR')}
                      &nbsp;· <strong>{s.hours_worked}h</strong>
                      {s.terminal && <> · {s.terminal}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${diffColor}`}>
                      {Math.abs(diff) < 0.01 ? '✓ Bateu' : (diff > 0 ? `+R$ ${diff.toFixed(2)} sobra` : `R$ ${diff.toFixed(2)}`)}
                    </span>
                    <Button onClick={() => handlePrint(s)} variant="outline" size="sm" className="border-indigo-500 text-indigo-700 hover:bg-indigo-50" data-testid={`shift-print-${s.id}`}>
                      <Printer size={14} className="mr-1" /> Imprimir
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-[10px] uppercase text-slate-500 font-bold">Vendas</p>
                    <p className="font-bold flex items-center gap-1"><TrendingUp size={12} /> {s.sales_count}</p>
                  </div>
                  <div className="bg-emerald-50 rounded p-2">
                    <p className="text-[10px] uppercase text-emerald-700 font-bold">Total vendido</p>
                    <p className="font-bold text-emerald-700 flex items-center gap-1"><DollarSign size={12} /> R$ {Number(s.total_sales).toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-[10px] uppercase text-slate-500 font-bold">Dinheiro esperado</p>
                    <p className="font-bold">R$ {Number(s.expected_amount).toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-[10px] uppercase text-slate-500 font-bold">Dinheiro contado</p>
                    <p className="font-bold">R$ {Number(s.counted_amount).toFixed(2)}</p>
                  </div>
                </div>

                {/* Breakdown por forma de pagamento */}
                {s.counted_breakdown && Object.keys(s.counted_breakdown).length > 0 && (
                  <details className="mt-3 group">
                    <summary className="cursor-pointer text-xs font-bold text-slate-700 hover:text-indigo-700 select-none">
                      Ver conciliação por forma de pagamento ▾
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-100 border-b border-slate-300">
                            <th className="text-left p-2">Forma</th>
                            <th className="text-right p-2">Esperado</th>
                            <th className="text-right p-2">Contado</th>
                            <th className="text-right p-2">Diferença</th>
                          </tr>
                        </thead>
                        <tbody>
                          {methodsToShow(s).map(m => {
                            const exp = Number((s.breakdown_expected || {})[m] || 0);
                            const cnt = Number((s.counted_breakdown || {})[m] || 0);
                            const d = Number((s.breakdown_difference || {})[m] || cnt - exp);
                            return (
                              <tr key={m} className="border-b border-slate-100">
                                <td className="p-2">{METHOD_LABELS[m] || m}</td>
                                <td className="text-right p-2">R$ {exp.toFixed(2)}</td>
                                <td className="text-right p-2">R$ {cnt.toFixed(2)}</td>
                                <td className={`text-right p-2 font-bold ${d === 0 ? '' : d > 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                                  {d >= 0 ? '+' : ''}R$ {d.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
    </div>
  );
};

export default ShiftSummaryPage;
